import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('usuarios');
  const [coordinaciones, setCoordinaciones] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [coordinadoresList, setCoordinadoresList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCoordModal, setShowCoordModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedCoordinacion, setSelectedCoordinacion] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  // Estados para formularios
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    nombre: '',
    role: 'capturista',
    coordinacion: ''
  });

  const [newCoord, setNewCoord] = useState({
    nombre: '',
    descripcion: ''
  });

  const [uploadData, setUploadData] = useState({
    coordinacionNombre: '',
    tipoArchivo: 'COORDINACIONES',
    file: null
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    await Promise.all([
      loadCoordinaciones(),
      loadUsuarios(),
      loadStats()
    ]);
  };

  const loadCoordinadores = async (coordinacionId) => {
    if (!coordinacionId) return;
    
    setLoading(true);
    try {
      // Cargar coordinadores
      const coordResponse = await api.get('/coordinadores', {
        params: { coordinacion: coordinacionId }
      });

      // Cargar delegados
      const delegadosResponse = await api.get('/delegados', {
        params: { coordinacion: coordinacionId }
      });
      
      // Cargar comité seccional
      const comiteResponse = await api.get('/comite-seccional', {
        params: { coordinacion: coordinacionId }
      });
      
      // Combinar ambos arrays, agregando campo 'tipo' para distinguirlos
      const coordinadoresConTipo = coordResponse.data.map(c => ({ ...c, tipo: 'coordinador' }));
      const delegadosConTipo = delegadosResponse.data.map(d => ({ ...d, tipo: 'delegado' }));
      const comiteConTipo = comiteResponse.data.map(c => ({ ...c, tipo: 'comite-seccional' }));
      
      setCoordinadoresList([...coordinadoresConTipo, ...delegadosConTipo, ...comiteConTipo]);
    } catch (error) {
      console.error('Error al cargar coordinadores:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCoordinaciones = async () => {
    try {
      const response = await api.get('/coordinaciones');
      setCoordinaciones(response.data);
    } catch (error) {
      console.error('Error al cargar coordinaciones:', error);
    }
  };

  const loadUsuarios = async () => {
    try {
      const response = await api.get('/users');
      setUsuarios(response.data);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    }
  };

  const loadStats = async () => {
    try {
      // Cargar estadísticas de coordinadores y delegados
      const coordResponse = await api.get('/coordinadores/stats/general');
      const delegadosResponse = await api.get('/delegados/stats/general');
      
      // Cargar estadísticas de comité seccional
      const comiteResponse = await api.get('/comite-seccional/stats/general');
      
      // Combinar estadísticas
      const totalCombinado = coordResponse.data.total + delegadosResponse.data.total + comiteResponse.data.total;
      const conFotoCombinado = coordResponse.data.conFoto + delegadosResponse.data.conFoto + comiteResponse.data.conFoto;
      const sinFotoCombinado = totalCombinado - conFotoCombinado;
      const porcentajeCombinado = totalCombinado > 0 
        ? Math.round((conFotoCombinado / totalCombinado) * 100) 
        : 0;
      
      setStats({
        total: totalCombinado,
        conFoto: conFotoCombinado,
        sinFoto: sinFotoCombinado,
        porcentaje: porcentajeCombinado
      });
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...newUser };
      if (payload.role === 'admin' || payload.role === 'asistencia') {
        delete payload.coordinacion;
      }
      await api.post('/users', payload);
      showMessage('success', 'Usuario creado correctamente');
      setShowModal(false);
      setNewUser({ username: '', password: '', nombre: '', role: 'capturista', coordinacion: '' });
      loadUsuarios();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error al crear usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      showMessage('success', 'Usuario eliminado correctamente');
      loadUsuarios();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error al eliminar usuario');
    }
  };

  const handleCreateCoordinacion = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/coordinaciones', newCoord);
      showMessage('success', 'Coordinación creada correctamente');
      setShowCoordModal(false);
      setNewCoord({ nombre: '', descripcion: '' });
      loadCoordinaciones();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error al crear coordinación');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadExcel = async (e) => {
    e.preventDefault();
    const requiereCoordinacion = uploadData.tipoArchivo !== 'DELEGADOS';

    if (!uploadData.file || (requiereCoordinacion && !uploadData.coordinacionNombre.trim())) {
      showMessage('error', requiereCoordinacion
        ? 'Selecciona un archivo e ingresa el nombre de la coordinación'
        : 'Selecciona un archivo para delegados');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', uploadData.file);
    if (requiereCoordinacion) {
      formData.append('coordinacionNombre', uploadData.coordinacionNombre.trim());
    }
    formData.append('tipoArchivo', uploadData.tipoArchivo);

    try {
      const response = await api.post('/upload/excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const coordinacionInfo = response.data.coordinacion?.nombre
        ? ` Coordinación: ${response.data.coordinacion.nombre}.`
        : '';
      showMessage('success', `${response.data.message}.${coordinacionInfo} Registros creados: ${response.data.registrosCreados}`);
      setShowUploadModal(false);
      setUploadData({ coordinacionNombre: '', tipoArchivo: 'COORDINACIONES', file: null });
      loadStats();
      loadCoordinaciones(); // Recargar para mostrar la nueva coordinación si se creó
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error al subir archivo');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReporte = async () => {
    setLoading(true);
    try {
      const response = await api.get('/reportes/fotos', {
        responseType: 'blob'
      });

      const contentType = response.headers['content-type']
        || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: contentType });
      const disposition = response.headers['content-disposition'];
      let filename = 'reporte_fotos.xlsx';

      if (disposition) {
        const match = /filename="?([^";]+)"?/i.exec(disposition);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showMessage('success', 'Reporte descargado correctamente');
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error al descargar reporte');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAsistencia = async () => {
    setLoading(true);
    try {
      const response = await api.get('/reportes/asistencia', { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const disposition = response.headers['content-disposition'];
      let filename = `reporte_asistencia_${new Date().toISOString().slice(0,10)}.xlsx`;
      if (disposition) {
        const match = /filename="?([^";]+)"?/i.exec(disposition);
        if (match && match[1]) filename = match[1];
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showMessage('success', 'Reporte de asistencia descargado correctamente');
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error al descargar reporte de asistencia');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePhoto = async (coordinadorId, tipo = 'coordinador') => {
    if (!window.confirm('¿Estás seguro de eliminar esta fotografía?')) return;
    
    try {
      await api.delete(`/upload/foto/${coordinadorId}`, {
        params: { tipo }
      });
      showMessage('success', 'Fotografía eliminada correctamente');
      loadCoordinadores(selectedCoordinacion);
      loadStats();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error al eliminar fotografía');
    }
  };

  const handleViewPhoto = (coord) => {
    setSelectedPhoto(coord);
    setShowPhotoModal(true);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  return (
    <div className="container">
      <h1>Panel de Administración</h1>

      {message.text && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'error'}`}>
          {message.text}
        </div>
      )}

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>{stats.total}</h3>
            <p>Total Registros</p>
          </div>
          <div className="stat-card">
            <h3>{stats.conFoto}</h3>
            <p>Con Fotografía</p>
          </div>
          <div className="stat-card">
            <h3>{stats.sinFoto}</h3>
            <p>Sin Fotografía</p>
          </div>
          <div className="stat-card">
            <h3>{stats.porcentaje}%</h3>
            <p>Progreso</p>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <button
          className="btn btn-primary"
          onClick={handleDownloadAsistencia}
          disabled={loading}
          style={{ fontSize: '1rem', padding: '12px 24px' }}
        >
          {loading ? 'Generando...' : '📋 Descargar Reporte de Asistencia'}
        </button>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'usuarios' ? 'active' : ''}`}
          onClick={() => setActiveTab('usuarios')}
        >
          Usuarios
        </button>
        <button 
          className={`tab ${activeTab === 'coordinaciones' ? 'active' : ''}`}
          onClick={() => setActiveTab('coordinaciones')}
        >
          Coordinaciones
        </button>
        <button 
          className={`tab ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          Subir Excel
        </button>
        <button 
          className={`tab ${activeTab === 'fotos' ? 'active' : ''}`}
          onClick={() => setActiveTab('fotos')}
        >
          Gestionar Fotos
        </button>
      </div>

      {activeTab === 'usuarios' && (
        <div className="card">
          <div className="card-header">
            <h2>Gestión de Usuarios</h2>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              + Nuevo Usuario
            </button>
          </div>
          <table>
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Nombre</th>
                <th>Rol</th>
                <th>Coordinación</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map(user => (
                <tr key={user._id}>
                  <td>{user.username}</td>
                  <td>{user.nombre}</td>
                  <td>{user.role}</td>
                  <td>{user.coordinacion?.nombre || '-'}</td>
                  <td>{user.activo ? 'Activo' : 'Inactivo'}</td>
                  <td>
                    <button 
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteUser(user._id)}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'coordinaciones' && (
        <div className="card">
          <div className="card-header">
            <h2>Coordinaciones</h2>
            <button className="btn btn-primary" onClick={() => setShowCoordModal(true)}>
              + Nueva Coordinación
            </button>
          </div>
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {coordinaciones.map(coord => (
                <tr key={coord._id}>
                  <td>{coord.nombre}</td>
                  <td>{coord.descripcion || '-'}</td>
                  <td>{coord.activo ? 'Activo' : 'Inactivo'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'upload' && (
        <div className="card">
          <h2>Subir Archivo Excel</h2>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
              Subir Archivo
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleDownloadReporte}
              disabled={loading}
            >
              {loading ? 'Generando...' : 'Descargar Reporte Fotos'}
            </button>
          </div>
          <div style={{ marginTop: '20px' }}>
            <h3>Instrucciones:</h3>
            <ul>
              <li>El archivo debe ser formato Excel (.xlsx o .xls)</li>
              <li><strong>COORDINACIONES:</strong> Columna 1 = Número, Columna 2 = Nombre</li>
              <li><strong>COMITÉ SECCIONAL:</strong> Columna 1 = Función, Columna 2 = Nombre</li>
              <li><strong>DELEGADOS:</strong> Columna 1 = Número, Columna 2 = Región, Columna 3 = Nombre, Columna 4 = Delegación/CT</li>
              <li><strong>Para COORDINACIONES y COMITÉ SECCIONAL</strong> escribe el nombre de la coordinación (si no existe, se creará automáticamente)</li>
              <li>Para DELEGADOS, la coordinación se asigna automáticamente por la región</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'fotos' && (
        <div className="card">
          <h2>Gestionar Fotografías</h2>
          <p className="info-text">Como administrador, puedes eliminar fotografías si es necesario</p>
          
          <div className="form-group" style={{ maxWidth: '400px', marginBottom: '20px' }}>
            <label>Seleccionar Coordinación</label>
            <select
              className="form-control"
              value={selectedCoordinacion}
              onChange={(e) => {
                setSelectedCoordinacion(e.target.value);
                loadCoordinadores(e.target.value);
              }}
            >
              <option value="">Seleccionar...</option>
              {coordinaciones.map(coord => (
                <option key={coord._id} value={coord._id}>{coord.nombre}</option>
              ))}
            </select>
          </div>

          {coordinadoresList.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Número/Función</th>
                  <th>Nombre</th>
                  <th>Región</th>
                  <th>Delegación/CT</th>
                  <th>Fotografía</th>
                  <th>Capturada por</th>
                  <th>Fecha</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {coordinadoresList.map(coord => (
                  <tr key={coord._id}>
                    <td>
                      {coord.tipo === 'comite-seccional' 
                        ? coord.funcion 
                        : `#${coord.numero}`}
                    </td>
                    <td>{coord.nombre}</td>
                    <td>{coord.tipo === 'delegado' ? coord.region : '-'}</td>
                    <td>{coord.tipo === 'delegado' ? coord.delegacionCt : '-'}</td>
                    <td>
                      {coord.fotografia ? (
                        <span className="badge badge-success">✓ Con foto</span>
                      ) : (
                        <span className="badge badge-pending">Sin foto</span>
                      )}
                    </td>
                    <td>{coord.capturadoPor?.nombre || '-'}</td>
                    <td>
                      {coord.fechaCaptura 
                        ? new Date(coord.fechaCaptura).toLocaleDateString() 
                        : '-'}
                    </td>
                    <td>
                      {coord.fotografia && (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => handleViewPhoto(coord)}
                          >
                            Ver Foto
                          </button>
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeletePhoto(coord._id, coord.tipo || 'coordinador')}
                          >
                            Eliminar Foto
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {selectedCoordinacion && coordinadoresList.length === 0 && !loading && (
            <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
              No hay registros en esta coordinación
            </p>
          )}
        </div>
      )}

      {/* Modal Crear Usuario */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Crear Nuevo Usuario</h2>
            <form onSubmit={handleCreateUser}>
              <div className="form-group">
                <label>Usuario</label>
                <input
                  type="text"
                  className="form-control"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Contraseña</label>
                <input
                  type="password"
                  className="form-control"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Nombre Completo</label>
                <input
                  type="text"
                  className="form-control"
                  value={newUser.nombre}
                  onChange={(e) => setNewUser({...newUser, nombre: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Rol</label>
                <select
                  className="form-control"
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                >
                  <option value="capturista">Capturista</option>
                  <option value="coordinador">Coordinador</option>
                  <option value="asistencia">Asistencia</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
              {(newUser.role === 'capturista' || newUser.role === 'coordinador') && (
                <div className="form-group">
                  <label>Coordinación</label>
                  <select
                    className="form-control"
                    value={newUser.coordinacion}
                    onChange={(e) => setNewUser({...newUser, coordinacion: e.target.value})}
                    required
                  >
                    <option value="">Seleccionar...</option>
                    {coordinaciones.map(coord => (
                      <option key={coord._id} value={coord._id}>{coord.nombre}</option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Creando...' : 'Crear Usuario'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Crear Coordinación */}
      {showCoordModal && (
        <div className="modal-overlay" onClick={() => setShowCoordModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Crear Nueva Coordinación</h2>
            <form onSubmit={handleCreateCoordinacion}>
              <div className="form-group">
                <label>Nombre</label>
                <input
                  type="text"
                  className="form-control"
                  value={newCoord.nombre}
                  onChange={(e) => setNewCoord({...newCoord, nombre: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  className="form-control"
                  value={newCoord.descripcion}
                  onChange={(e) => setNewCoord({...newCoord, descripcion: e.target.value})}
                  rows="3"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Creando...' : 'Crear Coordinación'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCoordModal(false)}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Subir Excel */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Subir Archivo Excel</h2>
            <form onSubmit={handleUploadExcel}>
              <div className="form-group">
                <label>Tipo de Archivo</label>
                <select
                  className="form-control"
                  value={uploadData.tipoArchivo}
                  onChange={(e) => setUploadData({...uploadData, tipoArchivo: e.target.value})}
                >
                  <option value="COORDINACIONES">COORDINACIONES</option>
                  <option value="DELEGADOS">DELEGADOS</option>
                  <option value="COMITE_SECCIONAL">COMITÉ SECCIONAL</option>
                </select>
              </div>
              <div className="form-group">
                <label>Nombre de la Coordinación</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: Coordinación Norte, Coordinación Sur..."
                  value={uploadData.coordinacionNombre}
                  onChange={(e) => setUploadData({...uploadData, coordinacionNombre: e.target.value})}
                  required={uploadData.tipoArchivo !== 'DELEGADOS'}
                  disabled={uploadData.tipoArchivo === 'DELEGADOS'}
                />
                <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                  {uploadData.tipoArchivo === 'DELEGADOS'
                    ? 'Para delegados, la coordinación se determina por la región del archivo'
                    : 'Si la coordinación no existe, se creará automáticamente'}
                </small>
              </div>
              <div className="form-group">
                <label>Archivo Excel</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".xlsx,.xls"
                  onChange={(e) => setUploadData({...uploadData, file: e.target.files[0]})}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Subiendo...' : 'Subir Archivo'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowUploadModal(false)}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Ver Fotografía */}
      {showPhotoModal && selectedPhoto && (
        <div className="modal-overlay" onClick={() => setShowPhotoModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h2>Fotografía de {selectedPhoto.nombre}</h2>
            <div style={{ marginBottom: '15px' }}>
              <p><strong>Número:</strong> {selectedPhoto.numero}</p>
              <p><strong>Capturada por:</strong> {selectedPhoto.capturadoPor?.nombre || 'N/A'}</p>
              <p><strong>Fecha:</strong> {selectedPhoto.fechaCaptura ? new Date(selectedPhoto.fechaCaptura).toLocaleDateString() : 'N/A'}</p>
            </div>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <img 
                src={selectedPhoto.fotografia?.startsWith('http') 
                  ? selectedPhoto.fotografia 
                  : `https://apisnte.apoyamos.mx/${selectedPhoto.fotografia}`}
                alt={selectedPhoto.nombre}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '400px', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              />
            </div>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => setShowPhotoModal(false)}
              style={{ width: '100%' }}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
